import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import moment from 'moment'

// components
import { CButton, CImage } from '@coreui/react-pro'
import Select from 'react-select';
import AppCard from '../../components/AppCard'
import AppTable from 'src/components/AppTable'
import AppModalConfirmation from 'src/components/modal/AppModalConfirmation'

// icons
import AddMoreIconWhite from '../../assets/icons/AddMoreIconWhite.png'

// styling
import { styleButtonVars2 } from '../../components/custom-styles/buttonStyles'
import { customStyles } from '../../components/custom-styles/selectStyles'

// helpers
import Axios from '../../helpers/axios'
import { columnLetterNumberList } from '../../components/table-column/TableColumnList'
import { formUrlQuery } from '../../helpers/format-queries'
import useToastMessage from 'src/helpers/toast-message'
import {
  singleLetterNumberOptionSelect,
  singleLetterNumberExportModeOptionSelect,
  singleMonthOptionSelect
} from 'src/helpers/format-options'
import { capitalizeFirstLetter } from 'src/helpers/text-format'

const LetterList = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { showSuccess, showError } = useToastMessage();
  const user = JSON.parse(localStorage.getItem('user')) || {}
  const roleIdUser = user?.role_id || null

  const isLoading = useSelector((state) => state.isLoading)

  const [showConfirmDelete, setShowConfirmDelete] = useState({ show: false, item: {} })

  const [letterList, setLetterList] = useState([]);
  const [letterListCount, setLetterListCount] = useState(0);
  const [letterListPerPage, setLetterListPerPage] = useState(10);
  const [activeLetterListPage, setActiveLetterListPage] = useState(1);
  const [searchLetterListFilter, setSearchLetterListFilter] = useState("");
  const [columnLetterListSorter, setColumnLetterListSorter] = useState({ column: "id", state: "desc" });

  const [columnFilter, setColumnFilter] = useState({})
  const [updateColumnList, setUpdateColumnList] = useState(columnLetterNumberList)

  const [isModalExportOpen, setIsModalExportOpen] = useState({ show: false });
  const [exportMode, setExportMode] = useState(null);
  const [selectedLetterType, setSelectedLetterType] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState([]);

  const letterFilterSections = [
    {
      key: 'type',
      label: 'Type',
      type: 'multi-checkbox',
      options: singleLetterNumberOptionSelect(),
    },
    {
      type: 'date-range',
      label: 'Date',
      startKey: 'start_date',
      endKey: 'end_date',
    },
  ]

  const formikExport = useFormik({
    initialValues: {
      mode: '',
      start_date: '',
      end_date: '',
      months: [],
      letter_types: [],
    },
    validationSchema: Yup.object().shape({
      mode: Yup.string().required("Please select export period."),
      start_date: Yup.string().when('mode', {
        is: 'date',
        then: (schema) => schema.required("Please select start date."),
      }),
      end_date: Yup.string().when('mode', {
        is: 'date',
        then: (schema) => schema.required("Please select end date."),
      }),
      months: Yup.array().when('mode', {
        is: 'monthly',
        then: (schema) => schema.min(1, "Please select month."),
      }),
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      handleExport(values)
      setSubmitting(false)
    },
  });

  useEffect(() => {
    let queries = {
      search: searchLetterListFilter,
      limit: letterListPerPage,
      offset: letterListPerPage * activeLetterListPage - letterListPerPage,
      sort:
        (columnLetterListSorter &&
          `${columnLetterListSorter.column}%${columnLetterListSorter.state}`) ||
        "",
    };
    if (columnFilter) {
      Object.keys(columnFilter).forEach((key) => {
        if (columnFilter[key]) {
          queries[key] = columnFilter[key].value
        } else {
          queries[key] = ''
        }
      })
    }
    queries = formUrlQuery(queries);
    fetchAllLetters(queries);
  }, [
    searchLetterListFilter,
    letterListPerPage,
    activeLetterListPage,
    columnLetterListSorter,
    columnFilter
  ]);

  useEffect(() => {
    if (columnFilter?.start_date?.value && columnFilter?.end_date?.value) {
      setActiveLetterListPage(1)
    }
  }, [columnFilter])

  useEffect(() => {
    let columnList = updateColumnList?.map((list) => {
      let data = list

      if (data.key == 'letter_type') {
        data.filter = (_, onChange) => {
          return (
            <Select
              options={singleLetterNumberOptionSelect()}
              value={columnFilter.type}
              onChange={(e) => {
                const selectedValue = e;
                onChange(selectedValue);
                setColumnFilter((prevState) => ({
                  ...prevState,
                  type: selectedValue
                }));
                setActiveLetterListPage(1)
              }}
              isClearable
              styles={customStyles}
            />
          )
        }
      }

      return data
    })

    setUpdateColumnList(columnList)
  }, [columnFilter, singleLetterNumberOptionSelect])

  const handleSearchItemsLetterList = (search) => {
    setActiveLetterListPage(1)
    setSearchLetterListFilter(search);
  }

  const fetchAllLetters = (queries) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "get",
      url: `/api/tracker/v1/generalsetting/letternumber/list?${queries}`
    })
      .then(({ data }) => {
        const tempLetterList = data?.value?.map((d) => ({
          ...d,
          letter_recipient: d?.letter_recipient || '-',
          letter_notes: d?.letter_notes ? capitalizeFirstLetter(d?.letter_notes) : '-',
        }))
        setLetterList(tempLetterList);
        if (activeLetterListPage === 1) {
          setLetterListCount(data?.count);
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      });
  };

  const handleDeleteConfirmation = () => {
    Axios({
      method: "delete",
      url: `/api/tracker/v1/generalsetting/delete/letternumber/${showConfirmDelete?.item?.id}`,
    })
      .then(({ data }) => {
        if (data?.success) {
          setShowConfirmDelete({ show: false, item: {} })
          fetchAllLetters(formUrlQuery({
            search: searchLetterListFilter,
            limit: letterListPerPage,
            offset: letterListPerPage * activeLetterListPage - letterListPerPage,
            sort:
              (columnLetterListSorter &&
                `${columnLetterListSorter.column}%${columnLetterListSorter.state}`) ||
              "",
          }))
          showSuccess('Letter number has been successfully deleted')
          window.location.reload()
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  }

  const handleOnRowLetterListClick = (item, columnName) => {
    if (columnName !== 'action' && columnName !== 'copy') {
      navigate(`/letter-no-tracker/details/${item.id}`)
    }
  }

  const copyToClipboard = async (val) => {
    try {
      await navigator.clipboard.writeText(val);
      showSuccess('Letter number has been copied to your clipboard')
    } catch (err) {
      showError('Failed to copy!')
    }
  };

  const handleSelectExportMode = (data) => {
    formikExport.setValues((values) => ({
      ...values,
      mode: data ? data.value : '',
    }))

    setExportMode(data || null)
  }

  const handleSelectLetterType = (data) => {
    formikExport.setValues((values) => ({
      ...values,
      letter_types: data ? data.map((item) => item?.value) : [],
    }))

    setSelectedLetterType(data || [])
  }

  const handleSelectMonth = (data) => {
    formikExport.setValues((values) => ({
      ...values,
      months: data ? data.map((item) => item?.value) : [],
    }))

    setSelectedMonth(data || [])
  }

  const handleOnDateChangeExport = (date, name) => {
    formikExport.setValues((values) => ({
      ...values,
      [name]: date ? moment(date).format('YYYY-MM-DD') : ''
    }), false)
  }

  const handleExport = async () => {
    const payload = {
      mode: formikExport.values.mode,
      letter_types: formikExport.values.letter_types,
      ...(formikExport.values.mode === 'monthly' ? { months: formikExport.values.months } : {}),
      ...(formikExport.values.mode === 'date'
        ? { start_date: formikExport.values.start_date, end_date: formikExport.values.end_date }
        : {}),
    };

    dispatch({ type: 'SET_IS_LOADING', isLoading: true });

    try {
      const { data } = await Axios({
        method: "post",
        url: `/api/tracker/v1/generalsetting/letternumber/export`,
        data: payload,
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob',
      });

      if (data) {
        const typeSegment = formikExport.values.letter_types.length > 0
          ? `-${formikExport.values.letter_types.join('-')}`
          : '';
        const periodSegment = {
          all: '',
          monthly: `-${formikExport.values.months
            .map((month) => moment().month(parseInt(month) - 1).format('MMM'))
            .join('-')}-${moment().format('YYYY')}`,
          date: `-${moment(formikExport.values.start_date).format('YYYYMMDD')}-${moment(formikExport.values.end_date).format('YYYYMMDD')}`
        }[formikExport.values.mode];
        const filename = `letter-number${typeSegment}${periodSegment}.xlsx`;

        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        showSuccess('Success export letter number');
        setIsModalExportOpen({ show: false });
        formikExport.resetForm();
        setExportMode(null);
        setSelectedLetterType([]);
        setSelectedMonth([]);
      }

    } catch (err) {
      console.error(err);
      let errMsg = 'Terjadi kesalahan saat export data';

      if (err.response && err.response.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          errMsg = json.message || json.error || errMsg;
          console.error('Server error:', json);
        } catch (parseError) {
          console.error('Gagal parsing error blob:', parseError);
        }
      } else if (err?.response?.data?.message) {
        errMsg = err.response.data.message;
      }

      showError(errMsg);
    } finally {
      dispatch({ type: 'SET_IS_LOADING', isLoading: false });
    }
  };

  return (
    <>
      {
        showConfirmDelete?.show && (
          <AppModalConfirmation 
            confirmationType='delete-item'
            itemName={`Letter number ${showConfirmDelete?.item?.letter_number}`}
            itemDetails={showConfirmDelete.item}
            itemType={showConfirmDelete.type}
            itemIndex={showConfirmDelete?.index}
            showModal={showConfirmDelete}
            setShowModal={setShowConfirmDelete}
            handleConfirmation={handleDeleteConfirmation}
          />
        )
      }
      {
        isModalExportOpen?.show && (
          <AppModalConfirmation
            confirmationType='export-letter-number'
            showModal={isModalExportOpen}
            setShowModal={setIsModalExportOpen}
            exportModeOptions={singleLetterNumberExportModeOptionSelect()}
            exportMode={exportMode}
            handleSelectExportMode={handleSelectExportMode}
            letterTypeOptions={singleLetterNumberOptionSelect()}
            selectedLetterType={selectedLetterType}
            handleSelectLetterType={handleSelectLetterType}
            monthOptions={singleMonthOptionSelect()}
            selectedMonth={selectedMonth}
            setSelectedMonth={handleSelectMonth}
            values={formikExport.values}
            onDateChange={handleOnDateChangeExport}
            errors={formikExport.errors}
            handleSubmit={formikExport.handleSubmit}
            isLoading={isLoading}
          />
        )
      }
      <div className='d-grid gap-2 d-md-flex justify-content-md-end mb-3'>
        <CButton style={styleButtonVars2} onClick={() => setIsModalExportOpen({ show: true })}>
          Export Letter Numbers
        </CButton>
        <CButton style={styleButtonVars2} onClick={() => navigate('/letter-no-tracker/add')}>
          <CImage fluid src={AddMoreIconWhite} className='me-2' />
          Add Letter Number
        </CButton>
      </div>
      <AppCard 
        className='mb-4'
        headerTitle={<h4>Letter Number List</h4>}
        bodyContent={
          <AppTable
            letterNumberListPage
            tableFilter
            setShowConfirmDelete={setShowConfirmDelete}
            tableColumns={columnLetterNumberList}
            tableContent={letterList}
            contentPerPage={letterListPerPage}
            activeContentPage={activeLetterListPage}
            contentCount={letterListCount}
            searchContentFilter={searchLetterListFilter}
            isShowButtonCopy={true}
            isShowButtonDelete={roleIdUser === 1}
            handleCopyToClipboard={copyToClipboard}
            setSearchContentFilter={handleSearchItemsLetterList}
            setActiveContentPage={setActiveLetterListPage}
            setContentPerPage={setLetterListPerPage}
            setColumnContentSorter={setColumnLetterListSorter}
            handleOnRowClick={handleOnRowLetterListClick}
            isClickable={true}
            columnFilter={columnFilter}
            setColumnFilter={setColumnFilter}
            filterSections={letterFilterSections}
            isShowFilterByPopover={true}
          />
        }
      />
    </>
  )
}

export default LetterList